/**
 * Intake Submission Seed Data
 *
 * Contains sample loan intake submissions for seeding via the intake.submit tRPC endpoint.
 * These represent completed wizard submissions from website visitors.
 *
 * DEPENDENCY: Requires intake templates to be configured (intake-templates.ts).
 *
 * @see Issue #432 - Templated Kiavi-Style Loan Intake Wizard
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';

// =============================================================================
// SEED IDs
// =============================================================================

export const INTAKE_SUBMISSION_IDS = {
  /** Bridge/Fix-Flip submission from Miami investor */
  BRIDGE_MIAMI: seedId('intake-sub-bridge', 1),
  /** Bridge submission from Fort Lauderdale investor */
  BRIDGE_FORTLAUDERDALE: seedId('intake-sub-bridge', 2),
  /** Construction submission from Tampa builder */
  CONSTRUCTION_TAMPA: seedId('intake-sub-construction', 3),
  /** DSCR rental submission from Orlando landlord */
  RENTAL_ORLANDO: seedId('intake-sub-rental', 4),
  /** Commercial submission from Jacksonville investor */
  COMMERCIAL_JACKSONVILLE: seedId('intake-sub-commercial', 5),
  /** General consultation request */
  GENERAL_INQUIRY: seedId('intake-sub-general', 6),
  /** Partner application from a mortgage broker */
  PARTNER_APP_BROKER: seedId('intake-sub-partner', 7),
} as const;

// =============================================================================
// DATA TYPES
// =============================================================================

export interface IntakeSubmissionSeedData {
  id: string;
  businessId: string;
  templateId: string;
  templateType: 'system' | 'custom';
  templateVersion: number;
  templateName: string;
  email: string;
  phone?: string;
  name?: string;
  intakeType:
    | 'bridge'
    | 'construction'
    | 'rental'
    | 'commercial'
    | 'general'
    | 'partner_invitation'
    | 'partner_onboarding';
  formData: Record<string, unknown>;
  loanAmount?: number;
  purchasePrice?: number;
  ltv?: number;
  propertyAddress?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyZip?: string;
  propertyType?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  sourceUrl?: string;
  stepsCompleted: number;
  totalSteps: number;
  completionTimeSeconds?: number;
}

// =============================================================================
// SUBMISSIONS DATA
// =============================================================================

export const INTAKE_SUBMISSIONS: Record<keyof typeof INTAKE_SUBMISSION_IDS, IntakeSubmissionSeedData> = {
  BRIDGE_MIAMI: {
    id: INTAKE_SUBMISSION_IDS.BRIDGE_MIAMI,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: 'bridge-v1',
    templateType: 'system',
    templateVersion: 1,
    templateName: 'Bridge/Fix & Flip',
    email: 'marcus.investor@gmail.com',
    phone: '(305) 555-0142',
    name: 'Marcus Rodriguez',
    intakeType: 'bridge',
    formData: {
      investmentType: 'bridge',
      entityName: 'Rodriguez Investments LLC',
      entityType: 'LLC',
      yearsExperience: 5,
      projectsCompleted: 12,
      propertyAddress: '1234 Ocean Dr',
      propertyCity: 'Miami Beach',
      propertyState: 'FL',
      propertyZip: '33139',
      propertyType: 'Single Family',
      purchasePrice: 475000,
      rehabBudget: 125000,
      afterRepairValue: 725000,
      loanAmount: 427500,
      loanTerm: 12,
      exitStrategy: 'sale',
      closingDate: '2026-03-15',
    },
    loanAmount: 427500,
    purchasePrice: 475000,
    ltv: 90,
    propertyAddress: '1234 Ocean Dr',
    propertyCity: 'Miami Beach',
    propertyState: 'FL',
    propertyZip: '33139',
    propertyType: 'Single Family',
    utmSource: 'google',
    utmMedium: 'cpc',
    utmCampaign: 'fix-flip-florida-2026',
    sourceUrl: 'https://coastal-funding.website.opsmatrix.dev/apply',
    stepsCompleted: 8,
    totalSteps: 8,
    completionTimeSeconds: 480,
  },

  BRIDGE_FORTLAUDERDALE: {
    id: INTAKE_SUBMISSION_IDS.BRIDGE_FORTLAUDERDALE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: 'bridge-v1',
    templateType: 'system',
    templateVersion: 1,
    templateName: 'Bridge/Fix & Flip',
    email: 'sarah.chen.realestate@outlook.com',
    phone: '(954) 555-0198',
    name: 'Sarah Chen',
    intakeType: 'bridge',
    formData: {
      investmentType: 'bridge',
      entityName: 'Sunrise Properties Group LLC',
      entityType: 'LLC',
      yearsExperience: 3,
      projectsCompleted: 6,
      propertyAddress: '567 Las Olas Blvd',
      propertyCity: 'Fort Lauderdale',
      propertyState: 'FL',
      propertyZip: '33301',
      propertyType: 'Townhouse',
      purchasePrice: 680000,
      rehabBudget: 95000,
      afterRepairValue: 920000,
      loanAmount: 612000,
      loanTerm: 9,
      exitStrategy: 'refinance',
    },
    loanAmount: 612000,
    purchasePrice: 680000,
    ltv: 90,
    propertyAddress: '567 Las Olas Blvd',
    propertyCity: 'Fort Lauderdale',
    propertyState: 'FL',
    propertyZip: '33301',
    propertyType: 'Townhouse',
    utmSource: 'facebook',
    utmMedium: 'social',
    sourceUrl: 'https://coastal-funding.website.opsmatrix.dev/apply',
    stepsCompleted: 8,
    totalSteps: 8,
    completionTimeSeconds: 620,
  },

  CONSTRUCTION_TAMPA: {
    id: INTAKE_SUBMISSION_IDS.CONSTRUCTION_TAMPA,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: 'construction-v1',
    templateType: 'system',
    templateVersion: 1,
    templateName: 'Ground-Up Construction',
    email: 'james.builder@tampabaybuilders.com',
    phone: '(813) 555-0276',
    name: 'James Walker',
    intakeType: 'construction',
    formData: {
      investmentType: 'construction',
      entityName: 'Tampa Bay Builders Inc',
      entityType: 'Corporation',
      yearsExperience: 15,
      projectsCompleted: 45,
      propertyAddress: '890 Bayshore Blvd',
      propertyCity: 'Tampa',
      propertyState: 'FL',
      propertyZip: '33606',
      propertyType: 'Single Family (Ground-Up)',
      lotValue: 180000,
      constructionBudget: 850000,
      afterCompletionValue: 1500000,
      loanAmount: 1020000,
      loanTerm: 18,
      hasPermits: true,
      hasContractor: true,
      hasPlans: true,
      hasBudget: true,
    },
    loanAmount: 1020000,
    purchasePrice: 180000,
    propertyAddress: '890 Bayshore Blvd',
    propertyCity: 'Tampa',
    propertyState: 'FL',
    propertyZip: '33606',
    propertyType: 'Single Family (Ground-Up)',
    utmSource: 'google',
    utmMedium: 'organic',
    sourceUrl: 'https://coastal-funding.website.opsmatrix.dev/services',
    stepsCompleted: 9,
    totalSteps: 9,
    completionTimeSeconds: 720,
  },

  RENTAL_ORLANDO: {
    id: INTAKE_SUBMISSION_IDS.RENTAL_ORLANDO,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: 'rental-dscr-v1',
    templateType: 'system',
    templateVersion: 1,
    templateName: 'Rental/DSCR',
    email: 'rachel.thompson.invest@gmail.com',
    phone: '(407) 555-0331',
    name: 'Rachel Thompson',
    intakeType: 'rental',
    formData: {
      investmentType: 'rental',
      entityName: 'Thompson Rentals LLC',
      entityType: 'LLC',
      yearsExperience: 8,
      unitsOwned: 14,
      propertyAddress: '456 International Dr',
      propertyCity: 'Orlando',
      propertyState: 'FL',
      propertyZip: '32819',
      propertyType: 'Duplex',
      purchasePrice: 520000,
      monthlyRent: 4200,
      monthlyExpenses: 1100,
      loanAmount: 416000,
      dscr: 1.35,
    },
    loanAmount: 416000,
    purchasePrice: 520000,
    ltv: 80,
    propertyAddress: '456 International Dr',
    propertyCity: 'Orlando',
    propertyState: 'FL',
    propertyZip: '32819',
    propertyType: 'Duplex',
    utmSource: 'referral',
    utmMedium: 'partner',
    sourceUrl: 'https://coastal-funding.website.opsmatrix.dev/apply',
    stepsCompleted: 8,
    totalSteps: 8,
    completionTimeSeconds: 540,
  },

  COMMERCIAL_JACKSONVILLE: {
    id: INTAKE_SUBMISSION_IDS.COMMERCIAL_JACKSONVILLE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: 'commercial-v1',
    templateType: 'system',
    templateVersion: 1,
    templateName: 'Commercial',
    email: 'mike.commercial@jaxproperties.com',
    phone: '(904) 555-0488',
    name: 'Michael Torres',
    intakeType: 'commercial',
    formData: {
      investmentType: 'commercial',
      entityName: 'Jacksonville Properties Partners LLC',
      entityType: 'LLC',
      yearsExperience: 12,
      commercialProjects: 8,
      propertyAddress: '789 Riverside Ave',
      propertyCity: 'Jacksonville',
      propertyState: 'FL',
      propertyZip: '32204',
      propertyType: 'Mixed-Use (Retail + 4 Units)',
      purchasePrice: 1800000,
      existingNOI: 156000,
      projectedNOI: 195000,
      loanAmount: 1260000,
      loanTerm: 24,
      zoningCompliant: true,
      hasEnvironmental: false,
    },
    loanAmount: 1260000,
    purchasePrice: 1800000,
    ltv: 70,
    propertyAddress: '789 Riverside Ave',
    propertyCity: 'Jacksonville',
    propertyState: 'FL',
    propertyZip: '32204',
    propertyType: 'Mixed-Use (Retail + 4 Units)',
    utmSource: 'linkedin',
    utmMedium: 'social',
    utmCampaign: 'commercial-lending-q1-2026',
    sourceUrl: 'https://coastal-funding.website.opsmatrix.dev/contact',
    stepsCompleted: 9,
    totalSteps: 9,
    completionTimeSeconds: 900,
  },

  GENERAL_INQUIRY: {
    id: INTAKE_SUBMISSION_IDS.GENERAL_INQUIRY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: 'general-v1',
    templateType: 'system',
    templateVersion: 1,
    templateName: 'General Consultation',
    email: 'new.investor@yahoo.com',
    phone: '(239) 555-0112',
    name: 'Kevin Patel',
    intakeType: 'general',
    formData: {
      investmentType: 'general',
      projectDescription:
        'First-time real estate investor looking to learn about fix and flip financing options in Southwest Florida. Have $100K cash to invest.',
      preferredContact: 'phone',
      bestTimeToCall: 'afternoon',
    },
    sourceUrl: 'https://coastal-funding.website.opsmatrix.dev/contact',
    stepsCompleted: 3,
    totalSteps: 3,
    completionTimeSeconds: 120,
  },

  PARTNER_APP_BROKER: {
    id: INTAKE_SUBMISSION_IDS.PARTNER_APP_BROKER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: 'partner-v1',
    templateType: 'system',
    templateVersion: 1,
    templateName: 'Partner Application',
    email: 'anna.broker@sunshinemortgage.com',
    phone: '(561) 555-0299',
    name: 'Anna Hernandez',
    intakeType: 'partner_onboarding',
    formData: {
      companyName: 'Sunshine Mortgage Group',
      businessType: 'Mortgage Broker',
      contactName: 'Anna Hernandez',
      contactTitle: 'VP of Production',
      statesLicensed: ['FL', 'GA', 'NC'],
      monthlyVolume: '$2M-$5M',
      partnershipType: 'broker',
      yearsInBusiness: 8,
      additionalInfo:
        'Looking to expand our private lending offerings. We currently originate $3M/month in conventional and are looking to add bridge and fix-flip products.',
    },
    utmSource: 'direct',
    sourceUrl: 'https://coastal-funding.website.opsmatrix.dev/partner-application',
    stepsCompleted: 2,
    totalSteps: 2,
    completionTimeSeconds: 300,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllIntakeSubmissions(): IntakeSubmissionSeedData[] {
  return Object.values(INTAKE_SUBMISSIONS);
}

export function getIntakeSubmissionsByType(
  intakeType: IntakeSubmissionSeedData['intakeType'],
): IntakeSubmissionSeedData[] {
  return Object.values(INTAKE_SUBMISSIONS).filter((s) => s.intakeType === intakeType);
}

export function getLoanIntakeSubmissions(): IntakeSubmissionSeedData[] {
  return Object.values(INTAKE_SUBMISSIONS).filter(
    (s) => !['partner_invitation', 'partner_onboarding'].includes(s.intakeType),
  );
}
